/**
 * API Client for Totemfeed
 * Handles all API communication with the Worker backend
 */

const API_BASE = import.meta.env.PROD
  ? "https://api.totemfeed.com"
  : "/api";

interface ApiError extends Error {
  status: number;
  statusText: string;
}

function createApiError(message: string, status: number, statusText: string): ApiError {
  const error = new Error(message) as ApiError;
  error.status = status;
  error.statusText = statusText;
  return error;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<T> {
  const url = `${API_BASE}${path}`;

  const options: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include", // Include cookies for session
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);

  // Handle non-2xx responses
  if (!response.ok) {
    let errorMessage = response.statusText;
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch {
      // Ignore JSON parse errors
    }
    throw createApiError(errorMessage, response.status, response.statusText);
  }

  // Parse JSON response
  return response.json() as Promise<T>;
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>("GET", path);
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>("POST", path, body);
  },

  put<T>(path: string, body?: unknown): Promise<T> {
    return request<T>("PUT", path, body);
  },

  delete<T>(path: string): Promise<T> {
    return request<T>("DELETE", path);
  },
};

// Specific API types
export interface SessionResponse {
  session_id: string;
  session_key: string;
}

export interface TotemResponse {
  id: string;
  question: string;
  period_start: string;
  period_end: string;
  total_paid_amount_cents: number;
  total_likes_count: number;
  total_answers_count: number;
  is_eligible_to_like: boolean;
}

export interface AnswerResponse {
  answer_id: string;
  content: string;
  created_at: string;
}

// Helper functions for specific endpoints
export async function createSession(): Promise<SessionResponse> {
  return api.post<SessionResponse>("/session");
}

export async function refreshSession(): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/session/refresh");
}

export async function getCurrentTotem(): Promise<TotemResponse> {
  return api.get<TotemResponse>("/totem/current");
}

export async function getRandomAnswer(totemId: string): Promise<AnswerResponse> {
  return api.get<AnswerResponse>(`/totem/${totemId}/random`);
}

export async function submitAnswer(
  totemId: string,
  content: string,
  isPaid: boolean,
  x402Proof?: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/answer", {
    totem_id: totemId,
    content,
    is_paid: isPaid,
    x402_proof: x402Proof,
  });
}

export async function likeAnswer(answerId: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/like", { answer_id: answerId });
}

export async function reportAnswer(
  answerId: string,
  reportType: "spam" | "hate" | "violent" | "other",
  details?: string
): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/report", {
    answer_id: answerId,
    report_type: reportType,
    details,
  });
}

